import { Map, RecordOf } from 'immutable';

import type { ImmutableANTLRNames, RecordOfBetEntity, RecordOfStakeEntity } from '../types';

import { getStakesNamesTemplatesByLang } from 'app/configs/stakesNamesTemplates';
import type { SupportedLang } from 'app/lang';
import { antlr } from 'app/packages/lib';

import { getIsANTLRErrors } from './get-is-antlr-errors';

// ---

const ANTLR_ERRORS = getIsANTLRErrors('create-antlr-names-helper');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type OptionalProps = {
  getStakeEntity: (entity: AnyType) => RecordOfStakeEntity;
  getBetEntity: (entity: AnyType) => RecordOfBetEntity;
};

type State = Map<number, RecordOf<Record<string, unknown>>>;

export default function createANTLRNames<S extends State, B extends State>(
  lang: SupportedLang,
  immutableObjects: Map<number, RecordOf<AnyType>>,
  stakesState: S,
  betsState: B,
  onUpdate: (type: 'stake' | 'bet', id: number, names: ImmutableANTLRNames) => void,
  optionalProps?: OptionalProps,
) {
  const templates = getStakesNamesTemplatesByLang(lang);
  let formatterContext: antlr.MatchNamesFormatterContext | null = null;

  const stakeParams: { [stakeId: string]: Map<string, unknown> } = {};
  const langForANTLR = antlr.transformToSupportedLanguage(lang);

  stakesState.forEach((entity) => {
    const stake = optionalProps?.getStakeEntity
      ? optionalProps.getStakeEntity(entity)
      : (entity as unknown as RecordOfStakeEntity);

    if (stake.type === undefined) {
      return;
    }

    const params = stake.params || {};
    stakeParams[stake.id] = params;

    if (formatterContext === null) {
      formatterContext = new antlr.MatchNamesFormatterContext();
      formatterContext.addObjects(immutableObjects);
    }

    const template = stake.type && templates?.[stake.type] || null;
    const names = template?.names || {};

    const stakeNames = antlr.NamesFormatter.Format(langForANTLR, formatterContext, params, names, {
      showErrors: ANTLR_ERRORS,
    });

    if (stakeNames) {
      onUpdate('stake', stake.id, Map(stakeNames) as ImmutableANTLRNames);
    }
  });

  betsState.forEach((entity) => {
    const bet = optionalProps?.getBetEntity
      ? optionalProps?.getBetEntity(entity)
      : (entity as unknown as RecordOfBetEntity);

    const stakeEntity = stakesState.get(bet.stakeId) || null;
    const stake = optionalProps?.getStakeEntity
      ? optionalProps.getStakeEntity(stakeEntity)
      : (stakeEntity as unknown as RecordOfStakeEntity);

    const stakeType = stake?.type;

    if (stakeType === undefined) {
      return;
    }

    if (formatterContext === null) {
      formatterContext = new antlr.MatchNamesFormatterContext();
      formatterContext.addObjects(immutableObjects);
    }

    let params = {};
    let names = {};

    if (stake !== null) {
      params = stakeParams[bet.stakeId] || stake.params;

      const template = templates?.[stakeType];

      if (template && bet.key && bet.key in template.bets) {
        names = template.bets[bet.key];
      }
    }

    formatterContext?.setErrorContext({
      names,
      stakeType,
      betKey: bet.key,
      betType: bet.type,
      matchId: bet.matchId,
    });

    const betNames = antlr.NamesFormatter.Format(langForANTLR, formatterContext, params, names, {
      showErrors: ANTLR_ERRORS,
    });

    if (betNames) {
      onUpdate('bet', bet.id, Map(betNames) as ImmutableANTLRNames);
    }
  });
}
